import { Injectable, Injector } from '@angular/core';
import { Router, NavigationSkipped, NavigationStart } from '@angular/router';

/**
 * Service to keep track of the previous page.
 * Use this service on your component to ask what was the previous page that led to your component
 */
@Injectable()
export class PreviousPageService {
  private fromNavigationBack: boolean;
  private currentUrl: string;
  private urls: Array<string> = [];
  private router: Router;

  constructor(private readonly inj: Injector) {
    // why having the injector and not the router? because this component is loaded in APP_INITIALIZER
    // and it lead to a cyclic dependency issue for @angular dependencies
  }

  /**
   * Get the previous visited url. Can be undefined
   */
  public getPreviousUrl() {
    return this.urls[this.urls.length - 1];
  }

  public popUrl(){
    this.urls.pop();
    sessionStorage.setItem('previousUrls', JSON.stringify(this.urls));
  }

  /**
   * Navigates to the last url, safely. This means it has no effect if there is no history back
   */
  public navigateBack(): void {
    if (this.hasHistory()) {
      this.fromNavigationBack = true;
      this.router.navigateByUrl(this.urls.pop());
      sessionStorage.setItem('previousUrls', JSON.stringify(this.urls));
    }
  }

  public navigateBackIgnoringPath(path: string): void {
    this.urls = this.urls.filter((url) => !url.includes(path));
    this.navigateBack();
  }

  /**
   * Tells if at least has one level of back history
   */
  public hasHistory(): boolean {
    return this.urls[this.urls.length - 1] !== undefined;
  }

  /**
   * Initialization method, it basic register to the navigation end event
   */
  public init() {
    this.router = this.inj.get(Router, null);

    if (sessionStorage.hasOwnProperty('previousUrls')) {
      this.urls = JSON.parse(sessionStorage.getItem('previousUrls'));
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart || event instanceof NavigationSkipped) {
        if (
          this.currentUrl &&
          !this.currentUrl.includes('token') &&
          !this.currentUrl.includes('content/preview') &&
          !this.currentUrl.includes('hg-asset/') &&
          !this.fromNavigationBack &&
          this.checkUrlDiffersFromPreviousOne(this.currentUrl, event.url) &&
          !this.checkSkipLocationChange()
        ) {
          this.urls.push(this.currentUrl);
          sessionStorage.setItem('previousUrls', JSON.stringify(this.urls));
        }
        if (event.url !=='/') this.currentUrl = event.url;
        this.fromNavigationBack = false;
      }
    });
  }

  /**
   * Adding URL in Session storage
   * Use this function when you open a new window and want to add the previous url
   */
  public addNavigation(url: string) {
    const urls = sessionStorage.hasOwnProperty('previousUrls')
      ? JSON.parse(sessionStorage.getItem('previousUrls'))
      : [];
    urls.push(url);
    sessionStorage.setItem('previousUrls', JSON.stringify(urls));
  }

  private checkUrlDiffersFromPreviousOne(currentUrl: string, nextUrl: string): boolean {
    const sanitizeUrl = (url: string) => url.replace(/^\/public/, '').split(/[#?]/g)[0];
    const nextUrlWithoutParams = sanitizeUrl(nextUrl);
    const currentUrlWithoutParams = sanitizeUrl(currentUrl);
    return nextUrlWithoutParams !== currentUrlWithoutParams;
  }

  private checkSkipLocationChange(): boolean {
    return this.router.getCurrentNavigation()?.extras?.skipLocationChange === true
  }
}
